// src/services/imageUploadService.js
/**
 * Image Upload Service - Handles image uploads to Firebase Storage
 * Future: Will integrate with AI image generation
 */

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import toast from 'react-hot-toast';

/**
 * Upload an image file to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} path - Storage path (e.g., 'companions/userId/companionId')
 * @returns {Promise<string>} Download URL of the uploaded image
 */
export const uploadImage = async (file, path) => {
  try {
    // Validate file
    if (!file) {
      throw new Error('No file provided');
    }

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.');
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      throw new Error('File too large. Maximum size is 5MB.');
    }

    // Create a unique filename with timestamp
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
    const fileName = `${timestamp}_${sanitizedFileName}`;
    const fullPath = `${path}/${fileName}`;

    // Create storage reference
    const storageRef = ref(storage, fullPath);

    // Upload file
    const uploadTask = await uploadBytes(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        uploadedAt: new Date().toISOString()
      }
    });

    // Get download URL
    const downloadURL = await getDownloadURL(uploadTask.ref);

    return downloadURL;

  } catch (error) {
    console.error('Error uploading image:', error);
    toast.error(error.message || 'Failed to upload image');
    throw error;
  }
};

/**
 * Upload character portrait image
 * @param {File} file - The image file
 * @param {string} userId - User ID
 * @param {string} characterId - Character ID
 * @returns {Promise<string>} Download URL
 */
export const uploadCharacterPortrait = async (file, userId, characterId) => {
  const path = `characters/${userId}/${characterId}`;
  return await uploadImage(file, path);
};

/**
 * Upload AI-generated character portrait from base64 data
 * @param {string} base64Data - Base64 encoded image data
 * @param {string} userId - User ID
 * @param {string} characterId - Character ID
 * @returns {Promise<string>} Download URL
 */
export const uploadAIGeneratedPortrait = async (base64Data, userId, characterId) => {
  try {
    // Convert base64 to blob
    const base64 = base64Data.replace(/^data:image\/\w+;base64,/, '');
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'image/png' });

    // Create File object
    const file = new File([blob], 'ai-generated-portrait.png', { type: 'image/png' });

    // Use existing uploadCharacterPortrait function
    return await uploadCharacterPortrait(file, userId, characterId);
  } catch (error) {
    console.error('Error uploading AI-generated portrait:', error);
    toast.error('Failed to save AI-generated portrait');
    throw error;
  }
};

/**
 * Upload companion image
 * @param {File} file - The image file
 * @param {string} userId - User ID
 * @param {string} companionId - Companion ID (optional, will be generated if not provided)
 * @returns {Promise<string>} Download URL
 */
export const uploadCompanionImage = async (file, userId, companionId = 'temp') => {
  const path = `companions/${userId}/${companionId}`;
  return await uploadImage(file, path);
};

/**
 * Validate image file before upload (client-side preview)
 * @param {File} file - The image file
 * @returns {Object} Validation result { valid: boolean, error: string|null, preview: string|null }
 */
export const validateImageFile = (file) => {
  const result = {
    valid: false,
    error: null,
    preview: null
  };

  if (!file) {
    result.error = 'No file selected';
    return result;
  }

  // Check file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    result.error = 'Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.';
    return result;
  }

  // Check file size
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    result.error = 'File too large. Maximum size is 5MB.';
    return result;
  }

  // Create preview URL
  result.preview = URL.createObjectURL(file);
  result.valid = true;

  return result;
};

/**
 * Future: Generate AI image for companion
 * This will be implemented when AI features are added
 * @param {string} prompt - Description of the companion
 * @param {string} userId - User ID
 * @param {string} companionId - Companion ID
 * @returns {Promise<string>} Download URL of generated image
 */
export const generateCompanionImage = async (prompt, userId, companionId) => {
  // TODO: Implement AI image generation
  // This is a placeholder for future AI integration
  throw new Error('AI image generation not yet implemented');
};
